import { useState, useCallback, RefObject } from 'react';
import { useLocation } from 'react-router-dom';
import { IconButton, Menu, MenuItem } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { exportToExcel, exportToCsv, exportTableToExcel, exportTableToExcelWithAllRows, getExcelFileNameFromPath } from '../../utils/excelExport';

type Props = {
  data?: Record<string, unknown>[] | (string | number)[][]; // 테이블 데이터 배열
  headers?: string[]; // 헤더 배열 (선택사항, data가 객체 배열이면 자동으로 키 사용)
  tableRef?: RefObject<HTMLTableElement | null>; // HTML 테이블 ref (선택사항, tableRef가 있으면 우선 사용)
  allRowsData?: (string | number)[][]; // 페이지네이션 무시하고 모든 행 데이터 (현재 정렬 상태 그대로, tableRef와 함께 사용)
  filename?: string; // 엑셀 파일명 (선택사항, 없으면 경로 기반 자동 생성)
  sheetName?: string; // 엑셀 시트명 (선택사항, 없으면 경로 기반 자동 생성)
  onDownloadExcel?: () => void; // 커스텀 엑셀 다운로드 핸들러 (선택사항)
  onDownloadCsv?: () => void; // 커스텀 CSV 다운로드 핸들러 (선택사항)
};

export default function TableActionsMenu({ data, headers, tableRef, allRowsData, filename, sheetName, onDownloadExcel, onDownloadCsv }: Props) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const location = useLocation();

  // 파일명과 시트명 결정 (props 우선, 없으면 경로 기반 자동 생성)
  const getExcelFileInfo = useCallback(() => {
    if (filename && sheetName) {
      return {
        filename: filename.endsWith('.xlsx') ? filename : `${filename}.xlsx`,
        sheetName
      };
    }
    return getExcelFileNameFromPath(location.pathname);
  }, [filename, sheetName, location.pathname]);

  // 테이블 ref를 사용한 엑셀 다운로드 (전체 데이터 포함)
  const handleTableRefDownloadExcel = useCallback(() => {
    if (!tableRef?.current) {
      return;
    }

    const fileInfo = getExcelFileInfo();
    
    // allRowsData가 제공되면 전체 데이터로 임시 테이블 생성
    if (allRowsData && allRowsData.length > 0) {
      exportTableToExcelWithAllRows(tableRef.current, allRowsData, fileInfo.filename, fileInfo.sheetName);
    } else {
      // allRowsData가 없으면 현재 테이블 상태 그대로 (페이지네이션된 데이터만)
      exportTableToExcel(tableRef.current, fileInfo.filename, fileInfo.sheetName);
    }
  }, [tableRef, allRowsData, getExcelFileInfo]);

  // 데이터를 엑셀 형식으로 자동 변환
  const handleAutoDownloadExcel = useCallback(() => {
    if (!data || data.length === 0) {
      return;
    }

    const fileInfo = getExcelFileInfo();

    // 데이터가 객체 배열인 경우
    if (typeof data[0] === 'object' && !Array.isArray(data[0])) {
      exportToExcel(data as Record<string, unknown>[], fileInfo.filename, fileInfo.sheetName, headers);
    } 
    // 데이터가 2D 배열인 경우
    else {
      const excelData = headers ? [headers, ...(data as (string | number)[][])] : (data as (string | number)[][]);
      exportToExcel(excelData, fileInfo.filename, fileInfo.sheetName);
    }
  }, [data, headers, getExcelFileInfo]);

  const handleAutoDownloadCsv = useCallback(() => {
    if (!data || data.length === 0) {
      return;
    }

    const fileInfo = getExcelFileInfo();
    const csvFilename = fileInfo.filename.replace('.xlsx', '.csv');

    // 데이터가 객체 배열인 경우
    if (typeof data[0] === 'object' && !Array.isArray(data[0])) {
      exportToCsv(data as Record<string, unknown>[], csvFilename, headers);
    } 
    // 데이터가 2D 배열인 경우
    else {
      const csvData = headers ? [headers, ...(data as (string | number)[][])] : (data as (string | number)[][]);
      exportToCsv(csvData, csvFilename);
    }
  }, [data, headers, getExcelFileInfo]);

  // 우선순위: 커스텀 핸들러 > 테이블 ref > 데이터 배열
  const handleExcel = onDownloadExcel || (tableRef ? handleTableRefDownloadExcel : handleAutoDownloadExcel);
  const handleCsv = onDownloadCsv || handleAutoDownloadCsv;

  return (
    <>
      <IconButton
        onClick={(e) => setAnchorEl(e.currentTarget)}
        aria-label="테이블 액션 메뉴"
        size="small"
      >
        <MoreVertIcon />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem 
          onClick={() => { handleExcel(); setAnchorEl(null); }} 
          disabled={!tableRef && !data && !onDownloadExcel}
        >
          엑셀 다운로드
        </MenuItem>
        <MenuItem 
          onClick={() => { handleCsv(); setAnchorEl(null); }} 
          disabled={!data && !onDownloadCsv}
        >
          CSV 다운로드
        </MenuItem>
      </Menu>
    </>
  );
}
